import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Achievement } from '../types';
import './AchievementsModal.css';

interface AchievementsModalProps {
    isOpen: boolean;
    onClose: () => void;
    achievements?: Achievement[];
}

const CATEGORY_LABELS: Record<string, { label: string; icon: string }> = {
    completion: { label: 'Completion', icon: '🏆' },
    streak: { label: 'Streak', icon: '🔥' },
    social: { label: 'Social', icon: '👥' },
};

export function AchievementsModal({ isOpen, onClose, achievements = [] }: AchievementsModalProps) {
    const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');

    // Reset filter when modal opens
    useEffect(() => {
        if (isOpen) {
            setFilter('all');
        }
    }, [isOpen]);

    const filteredAchievements = achievements.filter(a => {
        if (filter === 'unlocked') return a.unlocked;
        if (filter === 'locked') return !a.unlocked;
        return true;
    });

    const unlockedCount = achievements.filter(a => a.unlocked).length;
    const totalCount = achievements.length;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="achievements-modal-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className="achievements-modal"
                        initial={{ opacity: 0, scale: 0.92, x: '-50%', y: '-45%' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.92, x: '-50%', y: '-45%' }}
                        transition={{ type: 'spring', duration: 0.4 }}
                    >
                        <div className="achievements-modal-header">
                            <h2>🏆 Achievements</h2>
                            <button className="achievements-modal-close" onClick={onClose}>
                                ✕
                            </button>
                        </div>

                        <div className="achievements-summary">
                            <div className="achievements-progress-ring">
                                <svg viewBox="0 0 36 36" className="circular-chart">
                                    <path
                                        className="circle-bg"
                                        d="M18 2.0845
                                           a 15.9155 15.9155 0 0 1 0 31.831
                                           a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path
                                        className="circle-progress"
                                        strokeDasharray={`${(unlockedCount / Math.max(totalCount, 1)) * 100}, 100`}
                                        d="M18 2.0845
                                           a 15.9155 15.9155 0 0 1 0 31.831
                                           a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                </svg>
                                <div className="progress-ring-text">
                                    <span className="progress-count">{unlockedCount}</span>
                                    <span className="progress-total">/ {totalCount}</span>
                                </div>
                            </div>
                            <div className="achievements-summary-text">
                                <p className="summary-title">
                                    {unlockedCount === totalCount
                                        ? '🎉 All achievements unlocked!'
                                        : `${totalCount - unlockedCount} more to unlock`}
                                </p>
                                <p className="summary-subtitle">
                                    Keep playing to earn badges and rewards
                                </p>
                            </div>
                        </div>

                        <div className="achievements-filter">
                            <button
                                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                                onClick={() => setFilter('all')}
                            >
                                All
                            </button>
                            <button
                                className={`filter-btn ${filter === 'unlocked' ? 'active' : ''}`}
                                onClick={() => setFilter('unlocked')}
                            >
                                Unlocked ({unlockedCount})
                            </button>
                            <button
                                className={`filter-btn ${filter === 'locked' ? 'active' : ''}`}
                                onClick={() => setFilter('locked')}
                            >
                                Locked ({totalCount - unlockedCount})
                            </button>
                        </div>

                        <div className="achievements-grid">
                            {filteredAchievements.length === 0 ? (
                                <div className="achievements-empty">
                                    <p>No achievements to show.</p>
                                    <p className="empty-hint">Play more games to start earning!</p>
                                </div>
                            ) : (
                                filteredAchievements.map((achievement, index) => {
                                    const progressValue = Math.max(0, achievement.progress ?? 0);
                                    const targetValue = Math.max(1, achievement.target ?? 1);
                                    const clampedProgress = Math.min(progressValue, targetValue);
                                    const percent = Math.round((clampedProgress / targetValue) * 100);
                                    const categoryInfo = CATEGORY_LABELS[achievement.category || 'completion'] || CATEGORY_LABELS.completion;

                                    return (
                                        <motion.div
                                            key={achievement.id}
                                            className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'} ${achievement.just_unlocked ? 'just-unlocked' : ''}`}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: Math.min(index * 0.05, 0.3) }}
                                        >
                                            <div className="achievement-header">
                                                <span className="achievement-category">
                                                    {categoryInfo.icon} {categoryInfo.label}
                                                </span>
                                                <span className={`achievement-status ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
                                                    {achievement.unlocked ? '✓ Unlocked' : '🔒 Locked'}
                                                </span>
                                            </div>
                                            <div className="achievement-body">
                                                <h3 className="achievement-title">{achievement.name}</h3>
                                                <p className="achievement-desc">{achievement.description}</p>
                                                <div className="achievement-progress-container">
                                                    <div className="achievement-progress-track">
                                                        <div
                                                            className="achievement-progress-fill"
                                                            style={{ width: `${percent}%` }}
                                                        />
                                                    </div>
                                                    <span className="achievement-progress-text">
                                                        {clampedProgress} / {targetValue}
                                                    </span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default AchievementsModal;
